import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Fade,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SpeedIcon from '@mui/icons-material/Speed';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { format } from 'date-fns';
import {
  CARD_SX,
  ICON_RED,
  ICON_INDIGO,
  ICON_CYAN,
  formatCount,
} from '../pages/logsPage.utils';

export default function LogsBottomPanels({
  recentErrors,
  topSteps,
  topPhones,
  handleOpenModal,
  setFilterTraceId,
  handleQuickStepFilter,
  handleQuickPhoneFilter,
}) {
  return (
    <Fade in timeout={800}>
      <Grid container spacing={2}>
        {/* Alertas recentes */}
        <Grid item xs={12} md={6} lg={4}>
          <Box sx={CARD_SX}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                <Box sx={ICON_RED}><ErrorOutlineIcon sx={{ fontSize: 18 }} /></Box>
                <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
                  Alertas recentes
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
              {recentErrors.length === 0 ? (
                <Alert severity="success" sx={{ borderRadius: '12px' }}>Nenhum erro crítico nesta página.</Alert>
              ) : (
                <Stack spacing={2}>
                  {recentErrors.map((log) => (
                    <Box
                      key={log.id}
                      sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: 'rgba(239,83,80,0.06)',
                        border: '1px solid rgba(239,83,80,0.18)',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip label={log.step} color="error" size="small" sx={{ borderRadius: '10px', fontWeight: 700 }} />
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(log.created_at), 'dd/MM HH:mm:ss')}
                        </Typography>
                      </Stack>
                      <Typography variant="subtitle2" sx={{ mt: 1, color: '#0f172a' }}>
                        {log.message}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button size="small" onClick={() => handleOpenModal(log)} sx={{ borderRadius: '8px' }}>
                          Ver detalhes
                        </Button>
                        {log.trace_id && (
                          <Button size="small" color="error" onClick={() => setFilterTraceId(log.trace_id)} sx={{ borderRadius: '8px' }}>
                            Usar Trace ID
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Etapas com mais eventos */}
        <Grid item xs={12} md={6} lg={4}>
          <Box sx={CARD_SX}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                <Box sx={ICON_INDIGO}><SpeedIcon sx={{ fontSize: 18 }} /></Box>
                <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
                  Etapas com mais eventos
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
              {topSteps.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sem atividades suficientes para montar o ranking.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {topSteps.map(([step, count]) => (
                    <Stack
                      key={step}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1.2,
                        borderRadius: '12px',
                        bgcolor: 'rgba(99,102,241,0.06)',
                        border: '1px solid rgba(99,102,241,0.10)',
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#0f172a' }}>{step}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCount(count)} ocorrências
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleQuickStepFilter(step)}
                        sx={{
                          borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem',
                          borderColor: 'rgba(99,102,241,0.30)', color: '#6366f1',
                          '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' },
                        }}
                      >
                        Filtrar
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Telefones mais ativos */}
        <Grid item xs={12} lg={4}>
          <Box sx={CARD_SX}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Stack direction="row" alignItems="center" gap={1.25} mb={1}>
                <Box sx={ICON_CYAN}><PhoneIphoneIcon sx={{ fontSize: 18 }} /></Box>
                <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
                  Telefones mais ativos
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />
              {topPhones.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Ainda não há telefones suficientes nesta pesquisa.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {topPhones.map(([phone, count]) => (
                    <Stack
                      key={phone}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 1.2,
                        borderRadius: '12px',
                        bgcolor: 'rgba(8,145,178,0.06)',
                        border: '1px solid rgba(8,145,178,0.12)',
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#0f172a' }}>{phone}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCount(count)} registros
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleQuickPhoneFilter(phone)}
                        sx={{
                          borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem',
                          borderColor: 'rgba(8,145,178,0.30)', color: '#0891b2',
                          '&:hover': { bgcolor: 'rgba(8,145,178,0.08)' },
                        }}
                      >
                        Filtrar
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Fade>

  );
}
